import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { RootState } from '../../../reducers';
import * as actions from '../../../actions/generalActions';
import * as adminActions from '../../../actions/admin';
import * as productActions from '../../../actions/production';
import * as transportActions from '../../../actions/transport';
import { axiosCaller } from '../../../utils/apiCaller';
import { SelectOption } from '../../../types/selectOption.type';
import ArticlePageHeader from './components/ArticlePageHeader';
import ArticleForm, { ArticleFormState, DEFAULT_FORM_STATE, KubitRawMaterialOption } from './ArticleForm';
import { ChipOption } from './components/ChipMultiSelect';
import './articles.scss';

interface Props {
    history: any;
    actions: any;
    adminActions: any;
    productActions: any;
    transportActions: any;
    units: SelectOption[];
    departments: SelectOption[];
    admin: any;
    transport: any;
    production: any;
    companyProfile?: any;
    buttonLoader: boolean;
}

const toChipOptions = (list: any[]): ChipOption[] =>
    (list || []).map((item) => ({
        value: item.value ?? item.id,
        label: item.label ?? item.name ?? item.supplier ?? String(item.value ?? item.id),
    }));

// KuBIT raw-material options carry their consume responsibilities (for the
// "no consume step" warning). Normalize {value|id, label|name, consumeResponsibilities}.
const normalizeKubitRawMaterials = (list: any[]): KubitRawMaterialOption[] =>
    (list || []).map((r) => ({
        value: r.value ?? r.id,
        label: r.label ?? r.name ?? String(r.value ?? r.id),
        unitId: r.unitId ?? r.unit?.id ?? null,
        unitLabel: r.unit?.name ?? r.unit?.label ?? undefined,
        consumeResponsibilities: toChipOptions(r.consumeResponsibilities),
    }));

const ArticleCreatePage = (props: Props) => {
    const [initialValues, setInitialValues] = useState<Partial<ArticleFormState>>({ ...DEFAULT_FORM_STATE });
    const [bootstrapping, setBootstrapping] = useState(true);
    const [kubitRawMaterials, setKubitRawMaterials] = useState<KubitRawMaterialOption[]>([]);

    // KuBIT-only: the raw-material (type-3) articles selectable as this article's
    // KuBIT bill of materials. Fetched only for KuBIT tenants.
    useEffect(() => {
        if (!props.companyProfile?.kubitEnabled) return;
        axiosCaller('GET', 'articles/kubit-raw-materials')
            .then((res: any) => setKubitRawMaterials(normalizeKubitRawMaterials(res?.data?.data ?? res?.data)))
            .catch(() => undefined);
    }, [props.companyProfile?.kubitEnabled]);

    useEffect(() => {
        // Fetch every option list the form needs in parallel.
        props.actions.fetchUnits();
        props.actions.getAllDepartments();
        props.adminActions.fetchMaterials();
        props.adminActions.getSuppliers();
        props.transportActions.fetchEquipments();
        props.productActions.fetchResponsibilities();

        // Auto-generate the next product code.
        // Coerce to string: axios's default transformResponse JSON.parses bodies
        // that look numeric, so `"123"` from the backend arrives as the number
        // 123. The form treats productCode as a string (input + .trim() check).
        axiosCaller('GET', 'admin/articles/code')
            .then((res: any) => {
                const code = res?.data != null ? String(res.data) : '';
                setInitialValues((prev) => ({ ...prev, productCode: code }));
            })
            .catch(() => undefined)
            .finally(() => setBootstrapping(false));
    }, []);

    const suppliers: SelectOption[] = useMemo(
        () => (props.admin?.suppliers || []).map((s: any) => ({ value: s.id, label: s.supplier })),
        [props.admin?.suppliers],
    );

    // UnitResource returns {id, name}; normalize to {value, label} for the form.
    const units: SelectOption[] = useMemo(
        () => (props.units || []).map((u: any) => ({ value: u.value ?? u.id, label: u.label ?? u.name })),
        [props.units],
    );

    const materials: ChipOption[] = useMemo(() => toChipOptions(props.admin?.materials), [props.admin?.materials]);
    const equipments: ChipOption[] = useMemo(() => toChipOptions(props.transport?.equipments), [props.transport?.equipments]);
    const responsibilities: ChipOption[] = useMemo(
        () => toChipOptions(props.production?.responsibilities),
        [props.production?.responsibilities],
    );

    const goList = () => props.history.push('/admin/articles');

    const buildFormData = (form: ArticleFormState): FormData => {
        const fd = new FormData();
        fd.append('name', form.name);
        fd.append('productCode', form.productCode);
        fd.append('forProduction', String(form.forProduction));
        fd.append('description', form.description || '');
        fd.append('pricePerUnit', form.pricePerUnit !== '' ? String(form.pricePerUnit) : '');
        fd.append('costPerUnit', form.costPerUnit !== '' ? String(form.costPerUnit) : '');
        if (form.unitId != null) fd.append('unitId', String(form.unitId));
        if (form.kubitItemTypeId != null) fd.append('kubitItemTypeId', String(form.kubitItemTypeId));
        // != null so 0 (0% VAT) is appended, not dropped as falsy.
        if (form.kubitTaxId != null) fd.append('kubitTaxId', String(form.kubitTaxId));
        form.kubitConsumeResponsibilities.forEach((r) => fd.append('kubitConsumeResponsibilities[]', String(r.value)));
        // KuBIT bill of materials: raw-material component article id + template
        // quantity. Only meaningful for a production article; backend scopes/clears it.
        form.kubitComponents.forEach((c, i) => {
            fd.append(`kubitComponents[${i}][id]`, String(c.value));
            fd.append(`kubitComponents[${i}][quantity]`, String(c.quantity === '' ? 1 : c.quantity));
        });
        if (form.image) fd.append('image', form.image);

        if (form.forProduction === 1) {
            if (form.departmentId != null) fd.append('departmentId', String(form.departmentId));
            if (form.dailyCapacity !== '') fd.append('dailyCapacity', String(form.dailyCapacity));
            fd.append('extraTime', String(form.extraTime === '' ? 0 : form.extraTime));
            form.materials.forEach((m, i) => {
                fd.append(`materials[${i}][materialId]`, String(m.value));
                fd.append(`materials[${i}][quantity]`, String(m.quantity === '' ? 1 : m.quantity));
                if (m.unitId != null) fd.append(`materials[${i}][unitId]`, String(m.unitId));
            });
            form.equipments.forEach((e) => fd.append('equipments[]', String(e.value)));
            form.responsibilities.forEach((r) => fd.append('responsibilities[]', String(r.value)));
        } else {
            if (form.supplierId != null) fd.append('supplier[]', String(form.supplierId));
            fd.append('suppliersCode[0][code]', form.supplierCode || '');
            fd.append('deliveryDays', form.deliveryDays || '');
        }

        return fd;
    };

    const handleSubmit = (form: ArticleFormState) => {
        const fd = buildFormData(form);
        // createArticle returns true on success, false on a soft conflict
        // (duplicate productCode / name — backend returns HTTP 200 with a
        // `unique` flag instead of 4xx). Keep the form open on conflict so the
        // user can fix just the offending field instead of refilling from scratch.
        // Action already toasts both branches.
        return props.adminActions.createArticle(fd).then((ok: any) => {
            if (ok !== false) goList();
        });
    };

    return (
        <div className="art-page">
            <ArticlePageHeader
                onBack={goList}
                title={<FormattedMessage id="admin.articles.create.title" defaultMessage="Krijo artikull të ri" />}
            />

            {bootstrapping ? (
                // Gate the form behind the code-generate request — ArticleForm
                // seeds its internal useState from `initialValues` ONCE at mount,
                // so the auto-generated productCode must be present before the
                // form mounts. Mirrors the edit page's `articleHydrated` gate.
                <div className="art-edit-loading">
                    <FormattedMessage id="admin.articles.create.loading" defaultMessage="Duke përgatitur formularin..." />
                </div>
            ) : (
                <ArticleForm
                    mode="create"
                    initialValues={initialValues}
                    isLoading={!!props.buttonLoader}
                    kubitEnabled={!!props.companyProfile?.kubitEnabled}
                    units={units}
                    departments={props.departments || []}
                    suppliers={suppliers}
                    materials={materials}
                    equipments={equipments}
                    responsibilities={responsibilities}
                    kubitRawMaterials={kubitRawMaterials}
                    onSubmit={handleSubmit}
                    onCancel={goList}
                />
            )}
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        admin: state.admin,
        transport: state.transport,
        production: state.production,
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        adminActions: bindActionCreators(adminActions as any, dispatch),
        productActions: bindActionCreators(productActions as any, dispatch),
        transportActions: bindActionCreators(transportActions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ArticleCreatePage as any));
